import React, { useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { ShieldAlert, Trash2, RotateCcw } from "lucide-react";

/**
 * Admin-only Void / Restore action pair.
 * Renders nothing for non-Admin users.
 *
 * Props:
 *   entity: "indent" | "po" | "grn" | "invoice" | "pv" | "pr"
 *   id: entity id
 *   isVoided: boolean
 *   onDone: callback after action
 */
export function AdminVoidRestore({ entity, id, isVoided, onDone }) {
  const { hasRole } = useAuth();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  if (!hasRole("Administrator")) return null;

  const submit = async () => {
    if (!reason.trim()) { toast.error("Please enter a reason"); return; }
    setBusy(true);
    try {
      await api.post(`/admin/entity/${entity}/${id}/void`, { reason });
      toast.success("Document voided");
      setOpen(false); setReason("");
      onDone && onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const restore = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/entity/${entity}/${id}/restore`);
      toast.success("Restored");
      onDone && onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  if (isVoided) {
    return (
      <Button variant="outline" size="sm" onClick={restore} disabled={busy}
              data-testid={`admin-restore-${entity}`}>
        <RotateCcw size={14} className="mr-2" />Restore
      </Button>
    );
  }
  return (
    <>
      <Button variant="outline" size="sm" onClick={() => setOpen(true)}
              className="text-red-700 border-red-300 hover:bg-red-50"
              data-testid={`admin-void-${entity}`}>
        <Trash2 size={14} className="mr-2" />Void
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid={`admin-void-dialog-${entity}`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <ShieldAlert size={18} /> Void this document?
            </DialogTitle>
            <DialogDescription>
              This is an admin override. The document will remain in the audit log
              but be marked as voided and excluded from all downstream flows.
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label className="text-xs uppercase font-semibold">Reason *</Label>
            <Textarea
              value={reason} onChange={(e) => setReason(e.target.value)}
              rows={3} placeholder="Why is this being voided?"
              data-testid="admin-void-reason"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={submit} disabled={busy}
                    data-testid="admin-void-confirm">
              Confirm Void
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

/**
 * Admin-only banner shown when a document has been voided or admin-edited.
 */
export function AdminEditBanner({ doc }) {
  if (!doc) return null;
  if (doc.is_voided) {
    return (
      <div className="mb-4 p-3 rounded-md bg-red-50 dark:bg-red-950/30 text-red-800 dark:text-red-300 border border-red-200 dark:border-red-900 text-sm"
           data-testid="voided-banner">
        <strong>VOIDED</strong> — {doc.voided_reason || "(no reason)"}
        <span className="text-xs ml-2 text-red-600">by {doc.voided_by || "-"} on {(doc.voided_at || "").slice(0, 16).replace("T", " ")}</span>
      </div>
    );
  }
  if (doc.last_admin_edit_at) {
    return (
      <div className="mb-4 p-3 rounded-md bg-amber-50 dark:bg-amber-950/30 text-amber-800 dark:text-amber-300 border border-amber-200 dark:border-amber-900 text-xs"
           data-testid="admin-edit-banner">
        <strong>Admin Correction</strong> — {doc.last_admin_edit_reason || "(no reason)"}
        <span className="ml-2 opacity-70">by {doc.last_admin_edit_by} on {(doc.last_admin_edit_at || "").slice(0, 16).replace("T", " ")}</span>
      </div>
    );
  }
  return null;
}
